import { Alert } from "@mui/material";

import { useAnalytics } from "@/providers/analytics";
import {
  MetadataForm,
  MetadataView,
  type Metadata,
  type MetadataDiff,
} from "@/shared/components/Metadata";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { type SessionRecord } from "@/shared/domain/sessions";
import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./SessionMetadata.module.css";

interface SessionMetadataProps {
  session: SessionRecord;
}

interface ExistingMetadataProps {
  metadata: Metadata;
  sessionKey: string;
  disabled: boolean;
  onChange: (next: Metadata, diff?: MetadataDiff) => void;
  onDelete: (key: string) => void;
}

function ExistingMetadata({
  metadata,
  sessionKey,
  disabled,
  onChange,
  onDelete,
}: ExistingMetadataProps) {
  if (Object.keys(metadata).length === 0) {
    return null;
  }
  return (
    <MetadataView
      key={sessionKey}
      className={styles.metadataView}
      metadata={metadata}
      onChange={onChange}
      onDeleteMetadata={onDelete}
      disabled={disabled}
    />
  );
}

function MutationErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.inlineError}>
      {userFacingErrorMessage(error, "Failed to update session metadata.")}
    </Alert>
  );
}

export function SessionMetadata({ session }: SessionMetadataProps) {
  const { trackEvent } = useAnalytics();
  const { mutateAsync: updateSession, error, isPending } = useUpdateSession();

  async function handleAddMetadata(key: string, value: unknown) {
    trackEvent("AddSessionMetadataClicked", { sessionId: session.session_id });
    try {
      await updateSession({
        sessionId: session.session_id,
        updates: { metadata_changeset: { put_fields: { [key]: value } } },
      });
    } catch {
      // Swallowed: the mutation also exposes this error as `error`, which
      // MutationErrorAlert renders.
    }
  }

  async function handleRemoveMetadata(key: string) {
    trackEvent("RemoveSessionMetadataClicked", {
      sessionId: session.session_id,
    });
    try {
      await updateSession({
        sessionId: session.session_id,
        updates: { metadata_changeset: { remove_fields: [key] } },
      });
    } catch {
      // Swallowed: the mutation also exposes this error as `error`, which
      // MutationErrorAlert renders.
    }
  }

  async function handleMetadataChange(next: Metadata, diff?: MetadataDiff) {
    // A chip delete reaches both `onChange` (as `keysDeleted`) and
    // `onDeleteMetadata`; only the latter issues the remove mutation, so this
    // handler acts on `keysUpdated` alone.
    if (!diff?.keysUpdated?.length) {
      return;
    }
    const putFields: Record<string, unknown> = {};
    for (const key of diff.keysUpdated) {
      putFields[key] = next[key];
    }
    try {
      await updateSession({
        sessionId: session.session_id,
        updates: { metadata_changeset: { put_fields: putFields } },
      });
    } catch {
      // Swallowed: the mutation also exposes this error as `error`, which
      // MutationErrorAlert renders.
    }
  }

  return (
    <div className={styles.container}>
      <ExistingMetadata
        metadata={session.metadata}
        sessionKey={session.session_id}
        disabled={isPending}
        onChange={(next, diff) => void handleMetadataChange(next, diff)}
        onDelete={(key) => void handleRemoveMetadata(key)}
      />
      <MetadataForm
        key={`${session.session_id}-form`}
        className={styles.metadataForm}
        metadata={session.metadata}
        autocompleteType={AutocompleteType.SessionMetadataKeys}
        onAddMetadata={(key, value) => void handleAddMetadata(key, value)}
        disabled={isPending}
        isLoading={isPending}
      />
      <MutationErrorAlert error={error} />
    </div>
  );
}
